import { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Divider from "@mui/material/Divider";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { Box, FormControlLabel, Checkbox, List, ListItem, Typography, IconButton, Tooltip, Switch } from "@mui/material";
import { GeoJsonLayerConfig, LegendLayerConfig, MapLayerConfig } from "../../../types";
import { isFulcrumDerivedLayerConfig } from "../../../utilities/fulcrumLibService";
import { useLayerActions, useLayerSelector } from "../../../store/layer";
import { useMapActions } from "../../../store/map";
import { SPATIAL_ACTIVE_LAYER_ID } from "../../../constants/spatialDataset";
import { useEffectiveVisibleLayers } from "../../../hooks/useEffectiveVisibleLayers";
import { checkIfHasMultipleIcons, getFolderName, getGroupedLayersByType, getIconMappings, getSortedGroups } from "./helper";
import { isCmTableEndpoint } from "../../../utilities/cmTableEndpoint";
import { ApiEndpoint } from "../../../types";

interface LayersPanelProps {
  layers: MapLayerConfig[];
  /** Dynamic MapLibre layer specs synthesized from spatial CM Table endpoints. */
  cmTableLayers?: any[];
}

interface ILayerItemProps {
  layer: LegendLayerConfig;
  checked: boolean;
  /** True when the row is hidden by the active endpoint filter; caller uses this to dim + override click. */
  dimmed?: boolean;
  /**
   * Called with the visibility the user's click is requesting (the inverse of the current `checked`).
   * Callers must honour this explicit intent rather than re-toggling the real `visibleLayers` value,
   * because when the endpoint filter dims a row the `checked` the user sees is effective visibility,
   * not real visibility — a plain toggle would invert their intent when the underlying layer was
   * already on.
   */
  onVisibilityChange: (layerId: string, nextVisible: boolean) => void;
}

const DEFAULT_ICONS_HEIGHT = 20;
const DEFAULT_ICONS_WIDTH = 20;
const DEFAULT_CIRCLE_COLOR = "#3388ff";
const DEFAULT_CIRCLE_STROKE_COLOR = "#ffffff";
const DEFAULT_FILL_COLOR = "#aaa";
const DEFAULT_LINE_COLOR = "#3388ff";

const getStyleIcon = (layer: LegendLayerConfig, width: number, height: number) => {
  if (isFulcrumDerivedLayerConfig(layer)) {
    const iconUrl = layer.defaultIcon;
    return <img src={iconUrl} alt='Fulcrum layer' style={{ width: `${width}px`, height: `${height}px` }} />;
  }

  // For raster layers, return a generic icon
  if (layer.type !== "geojson") {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill='none' xmlns='http://www.w3.org/2000/svg'>
        <rect width={width} height={height} fill={DEFAULT_FILL_COLOR} opacity={0.5} />
      </svg>
    );
  }

  const viewBox = `0 0 ${width} ${height}`;

  // For GeoJSON based on geometry type
  switch (layer.geometry) {
    case "symbol":
      // Return an SVG rectangle with the layer color or default color
      return (
        <svg width={width} height={height} viewBox={viewBox} fill='none' xmlns='http://www.w3.org/2000/svg'>
          <circle
            cx={width / 2}
            cy={height / 2}
            r={width / 3}
            fill={layer.style["circle-color"] || layer.style.iconStyle?.color || DEFAULT_CIRCLE_COLOR}
            stroke={layer.style["circle-stroke-color"] || layer.style.iconStyle?.color || DEFAULT_CIRCLE_STROKE_COLOR}
            strokeWidth='2'
          />
        </svg>
      );
    case "line":
      // Simple line for line type
      return (
        <svg width={width} height={height} viewBox={viewBox} fill='none' xmlns='http://www.w3.org/2000/svg'>
          <line
            x1='4'
            y1={height / 2}
            x2={width - 4}
            y2={height / 2}
            stroke={layer.style["line-color"] || DEFAULT_LINE_COLOR}
            strokeWidth={layer.style["line-width"] || 3}
            strokeLinecap='round'
          />
        </svg>
      );
    case "fill":
      // Rectangle for fill type
      return (
        <svg width={width} height={height} viewBox={viewBox} fill='none' xmlns='http://www.w3.org/2000/svg'>
          <rect
            x='2'
            y='2'
            width={width - 4}
            height={height - 4}
            fill={layer.style["circle-color"] || DEFAULT_CIRCLE_COLOR}
            fillOpacity={layer.style.opacity || 0.6}
            stroke='#ffffff'
            strokeWidth='1'
          />
        </svg>
      );
    case "circle":
      // Circle for point type
      return (
        <svg width={width} height={height} viewBox={viewBox} fill='none' xmlns='http://www.w3.org/2000/svg'>
          <circle
            cx={width / 2}
            cy={height / 2}
            r={layer.style["circle-radius"] || width / 3}
            fill={layer.style["circle-color"] || DEFAULT_CIRCLE_COLOR}
            fillOpacity={layer.style.opacity || 0.8}
            stroke={layer.style["circle-stroke-color"] || DEFAULT_CIRCLE_STROKE_COLOR}
            strokeWidth={layer.style["circle-stroke-width"] || 2}
          />
        </svg>
      );
    default:
      // Default icon for other types
      return (
        <svg width={width} height={height} viewBox={viewBox} fill='none' xmlns='http://www.w3.org/2000/svg'>
          <rect x='2' y='2' width={width - 4} height={height - 4} fill='#aaa' stroke='#666' strokeWidth='1' />
        </svg>
      );
  }
};

// list icons with name
const ListAllIcons = ({ layer }: { layer: GeoJsonLayerConfig }) => {
  const iconsList = useMemo(() => {
    return getIconMappings(layer as GeoJsonLayerConfig);
  }, [layer]);

  return (
    <>
      <List sx={{ display: "flex", flexDirection: "column", gap: 2 }} disablePadding>
        {iconsList?.map((icon) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <img src={icon.icon} alt={`${icon.value} icon`} style={{ width: "16px", height: "16px" }} />
            <Typography variant='caption' sx={{ fontSize: "0.7rem" }}>
              {icon.value}
            </Typography>
          </Box>
        ))}
      </List>
      <Divider color='#344054' sx={{ marginY: "8px" }} />
    </>
  );
};

// render list of layers display icons at the end if it has multiple icons show display expand icon
const LayerItem = ({ layer, checked, dimmed = false, onVisibilityChange }: ILayerItemProps) => {
  const [isOpen] = useState(true); // always open can be maintained later
  const hasMultipleIcons = checkIfHasMultipleIcons(layer);
  const displayName = (layer as any).name ?? layer.id;
  // Tooltip explains the dimmed state so users aren't confused about why the row looks inactive.
  const tooltipTitle = dimmed
    ? `${displayName} (hidden by endpoint filter — click to also show this layer alongside the selected endpoint)`
    : displayName;

  return (
    <Box sx={{ opacity: dimmed ? 0.5 : 1, transition: "opacity 150ms ease" }}>
      <Tooltip title={tooltipTitle}>
        <FormControlLabel
          labelPlacement='start'
          control={<Switch checked={checked} onChange={() => onVisibilityChange(layer.id, !checked)} size='small' />}
          label={
            <Box sx={{ width: "9rem", display: "flex", alignItems: "center", justifyContent: "start", gap: 8 }}>
              {!hasMultipleIcons && getStyleIcon(layer, DEFAULT_ICONS_WIDTH, DEFAULT_ICONS_HEIGHT)}

              <Typography variant='body2' sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayName}
              </Typography>
            </Box>
          }
          sx={{ width: "10rem", color: "white" }}
        />
      </Tooltip>
      {isOpen && hasMultipleIcons && <ListAllIcons layer={layer as GeoJsonLayerConfig} />}
    </Box>
  );
};

// List the layers
const RenderLegendByGroup = ({
  title,
  layers,
  isLastItem,
  openedPanel,
  togglePanel
}: {
  title: string;
  layers: LegendLayerConfig[];
  isLastItem: boolean;
  openedPanel: string | null;
  togglePanel: (title: string | null) => void;
}) => {
  const { setLayerVisibility } = useLayerActions();
  const { visibleLayers } = useLayerSelector();
  const { setShowOnlySelectedEndpointsDataOnMap } = useMapActions();
  const { effectiveVisibleLayers, isFilteredByEndpoint, endpointLayerId } = useEffectiveVisibleLayers();

  // All panel indicators (row switches, group checkbox) reflect EFFECTIVE visibility so the UI
  // matches what the map is actually drawing. When the endpoint filter is active, non-endpoint
  // rows are effectively off and get dimmed; clicking them clears the filter and applies the
  // visibility the user's click was asking for.
  const allLayersVisible = layers.every((layer) => effectiveVisibleLayers.has(layer.id));
  const someLayersVisible = layers.some((layer) => effectiveVisibleLayers.has(layer.id));

  // Individual row click: always drive toward `nextVisible` explicitly (never a toggle against the
  // real store state). When the user sees an unchecked, dimmed row and clicks it, they want that
  // layer ON — regardless of whether it was already on underneath; a plain toggle would invert
  // their intent.
  //
  // Clicking a dimmed row additionally switches visibility modes: we want to end up with JUST
  // {endpoint, clicked} visible, not the full pre-filter set. If we merely flipped the flag, any
  // layers that were on before the filter was enabled would suddenly re-appear alongside the
  // clicked one — violating the user's "only these two" intent. So we first clear out real
  // visibility, restore the endpoint, then turn the clicked layer on.
  const handleRowChange = (layerId: string, nextVisible: boolean) => {
    if (isFilteredByEndpoint && layerId !== endpointLayerId) {
      visibleLayers.forEach((id) => {
        if (id !== endpointLayerId) setLayerVisibility(id, false);
      });
      if (endpointLayerId) setLayerVisibility(endpointLayerId, true);
      setLayerVisibility(layerId, nextVisible);
      setShowOnlySelectedEndpointsDataOnMap(false);
      return;
    }
    setLayerVisibility(layerId, nextVisible);
  };

  // Group checkbox: while the filter is active, group-level semantics are ambiguous (the displayed
  // "partial" state reflects only the endpoint layer). Keep it predictable — a click clears the
  // filter without applying a bulk toggle. Users can then use the group checkbox normally.
  const toggleGroupVisibility = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFilteredByEndpoint) {
      setShowOnlySelectedEndpointsDataOnMap(false);
      return;
    }

    const checked = e.target.checked;
    layers.forEach((layer) => setLayerVisibility(layer.id, checked));
  };

  const groupCheckboxTooltip = isFilteredByEndpoint ? "Endpoint filter active — click to clear filter" : "";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Tooltip title={groupCheckboxTooltip}>
            <Checkbox
              checked={allLayersVisible}
              indeterminate={someLayersVisible && !allLayersVisible}
              indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: "white" }} />}
              onChange={toggleGroupVisibility}
              sx={{
                color: "rgba(252, 252, 253, 0.5)",
                "&.Mui-checked": { color: "white" },
                accentColor: "white",
                ":indeterminate": { color: "white", accentColor: "white" },
                padding: 0
              }}
            />
          </Tooltip>
          <Typography variant='h6' fontStyle='italic'>
            {title}
          </Typography>
        </Box>

        <IconButton onClick={() => togglePanel(title)} sx={{ color: "white" }}>
          {title === openedPanel ? <ExpandLessIcon fontSize='large' /> : <ExpandMoreIcon fontSize='large' />}
        </IconButton>
      </Box>
      {title === openedPanel && (
        <List sx={{ overflowY: "auto", flexGrow: 1, display: "flex", flexDirection: "column", gap: 10 }} disablePadding>
          {layers.map((layer) => {
            const checked = effectiveVisibleLayers.has(layer.id);
            const dimmed = isFilteredByEndpoint && layer.id !== endpointLayerId;
            return (
              <ListItem key={layer.id} sx={{ py: 0 }}>
                <LayerItem key={layer.id} checked={checked} dimmed={dimmed} layer={layer} onVisibilityChange={handleRowChange} />
              </ListItem>
            );
          })}
        </List>
      )}
      {!isLastItem && <Divider color='#344054' sx={{ marginTop: "10px" }} />}
    </Box>
  );
};

/**
 * Synthesise a minimal GeoJsonLayerConfig-shaped display object for a CM Table MapLibre layer.
 * LayersPanel only reads `id`, `type`, `geometry`, `style`, and `initialVisibility` for rendering.
 */
function synthesiseCmTableDisplayLayer(cmLayer: any): any {
  const spatialType = (cmLayer as any)._spatialType as string | undefined;
  let geometry: GeoJsonLayerConfig["geometry"] = "circle";
  if (spatialType === "polygon" || spatialType === "multipolygon") geometry = "fill";
  else if (spatialType === "linestring" || spatialType === "multilinestring") geometry = "line";

  return {
    id: cmLayer.id,
    name: cmLayer.displayName ?? cmLayer.id,
    type: "geojson",
    url: "", // not used for display
    geometry,
    initialVisibility: (cmLayer as any).initialVisibility !== false,
    defaultIcon: "",
    style: {
      "circle-color": cmLayer.paint?.["circle-color"] ?? cmLayer.paint?.["fill-color"] ?? cmLayer.paint?.["line-color"] ?? "#00c2ff",
      "circle-radius": cmLayer.paint?.["circle-radius"] ?? 6,
      "circle-stroke-width": 1,
      "circle-stroke-color": "#ffffff"
    }
  } as any;
}

const LayersPanel = ({ layers, cmTableLayers = [] }: LayersPanelProps) => {
  // Synthesise display entries for each active spatial CM Table layer.
  // Skip mixed-geometry sibling layers (::line / ::fill) so a "geometry" table
  // shows a single toggle row (the base point layer represents the table).
  const cmDisplayLayers = useMemo(
    () => cmTableLayers.filter((l) => l._spatialType && l._spatialType !== "none" && !l._cmSubLayer).map(synthesiseCmTableDisplayLayer),
    [cmTableLayers]
  );

  const groupedLayers = useMemo(() => {
    const baseGroups = getGroupedLayersByType(layers);

    // Always inject a "Spatial Datasets" toggle into the overlays group.
    // Active spatial layers have type "cog" or "pmtiles" which getGroupedLayersByType
    // filters out, so we inject the entry directly from state.layers.
    // When no dataset is loaded yet the synthetic placeholder still appears (off/unchecked)
    // so users can see the control is there; once a dataset is selected it toggles normally.
    const activeSpatialLayer = layers.find((l) => l.id === SPATIAL_ACTIVE_LAYER_ID);
    const spatialEntry: LegendLayerConfig = activeSpatialLayer
      ? ({ ...activeSpatialLayer, name: "Spatial Datasets" } as any)
      : ({
          id: SPATIAL_ACTIVE_LAYER_ID,
          name: "Spatial Datasets",
          type: "raster",
          initialVisibility: false,
          style: {}
        } as any);

    if (baseGroups["overlays"]) {
      // Strip any spatial-* entries that somehow slipped through the type filter
      const nonSpatial = baseGroups["overlays"].filter((l) => !l.id.startsWith("spatial-"));
      baseGroups["overlays"] = [...nonSpatial, spatialEntry];
    } else {
      baseGroups["overlays"] = [spatialEntry];
    }

    if (cmDisplayLayers.length > 0) {
      baseGroups["overlays"] = [...(baseGroups["overlays"] || []), ...cmDisplayLayers];
    }
    return baseGroups;
  }, [layers, cmDisplayLayers]);

  const sortedGroups = useMemo(() => {
    return getSortedGroups(Object.keys(groupedLayers));
  }, [groupedLayers]);

  // Default the sole group to expanded when only one group is present; otherwise start collapsed.
  const [openedPanel, setOpenedPanel] = useState<string | null>(() => (sortedGroups.length === 1 ? getFolderName(sortedGroups[0]) : null));

  // If groups appear/disappear at runtime (e.g. after Fulcrum preload), keep the "auto-open the
  // only group" behaviour without stomping on explicit user interaction once multiple groups exist.
  useEffect(() => {
    if (sortedGroups.length === 1) {
      const onlyTitle = getFolderName(sortedGroups[0]);
      if (openedPanel !== onlyTitle) setOpenedPanel(onlyTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedGroups.join("|"), cmDisplayLayers.length]);

  const togglePanel = (title: string | null) => {
    if (title === openedPanel) return setOpenedPanel(null);
    if (title !== openedPanel) return setOpenedPanel(title);
  };

  return (
    <Box
      sx={{
        width: "14rem",
        backgroundColor: "#1D2939",
        color: "white",
        border: "1px solid rgba(252, 252, 253, 0.15)",
        borderRadius: "0.5rem",
        padding: "0.625rem 0.75rem 0.75rem 0.75rem",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "40rem",
        overflowY: "scroll",
        gap: "0.625rem"
      }}
    >
      {sortedGroups.map((type: string, index: number) => (
        <RenderLegendByGroup
          key={type}
          title={getFolderName(type)}
          layers={groupedLayers[type]}
          isLastItem={index === sortedGroups?.length - 1}
          openedPanel={openedPanel}
          togglePanel={togglePanel}
        />
      ))}
    </Box>
  );
};

export default LayersPanel;
